"use client";

import { ArrowRight, CircleCheck as CheckCircle2, Loader as Loader2, LockKeyhole } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
const COUNTRIES = ["Japan", "South Korea", "Thailand", "Vietnam", "Malaysia", "Indonesia", "Singapore", "Other"];
const PLAYING_LEVELS = ["Beginner (Handicap 25+)", "Intermediate (Handicap 10-24)", "Advanced (Handicap < 10)", "Professional"];
const TRAVEL_INTERESTS = ["Luxury Golf Resorts", "Championship Courses", "Budget-Friendly Trips", "Group Tours & Events"];
const fieldLabelClass = "mb-2 block text-[12px] font-semibold uppercase tracking-[0.08em] text-[#274E43]";
const inputClass = "!h-[54px] rounded-[9px] border-[#D9CFBE] bg-[#FFFDF9] px-4 text-[14px] text-[#073F31] shadow-none placeholder:text-[#9AA39F] focus-visible:border-[#8EAA70] focus-visible:ring-2 focus-visible:ring-[#BFFF00]/35";
const selectTriggerClass = "!h-[54px] w-full rounded-[9px] border-[#D9CFBE] bg-[#FFFDF9] px-4 text-[14px] text-[#073F31] shadow-none focus:border-[#8EAA70] focus:ring-2 focus:ring-[#BFFF00]/35";
function RegistrationFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <Alert className="rounded-[12px] border border-[#C8D8B8] bg-[#F4F8EA] px-5 py-5 text-[#073F31] shadow-none">
        <CheckCircle2 aria-hidden="true" className="size-5 text-[#0B694F]" strokeWidth={1.8} />

        <AlertTitle className="mb-1 text-[20px] font-normal text-[#073F31]" style={{
        fontFamily: "'Playfair Display', Georgia, serif"
      }}>
          Thank you for joining TeeListClub
        </AlertTitle>

        <AlertDescription className="text-[14px] leading-[1.75] text-[#506B63]" style={{
        fontFamily: "'Raleway', Arial, sans-serif"
      }}>
          {successMessage}
        </AlertDescription>
      </Alert>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5" style={{
    fontFamily: "'Raleway', Arial, sans-serif"
  }}>
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <FormField control={control} name="firstName" rules={{
        required: "First name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className={fieldLabelClass}>
                First Name
              </FormLabel>

              <FormControl>
                <Input placeholder="Enter your first name" autoComplete="given-name" className={inputClass} {...field} />
              </FormControl>

              <FormMessage className="mt-1 text-[12px]" />
            </FormItem>} />

        <FormField control={control} name="lastName" rules={{
        required: "Last name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className={fieldLabelClass}>
                Last Name
              </FormLabel>

              <FormControl>
                <Input placeholder="Enter your last name" autoComplete="family-name" className={inputClass} {...field} />
              </FormControl>

              <FormMessage className="mt-1 text-[12px]" />
            </FormItem>} />
      </div>

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className={fieldLabelClass}>
              Email Address
            </FormLabel>

            <FormControl>
              <Input type="email" placeholder="you@example.com" autoComplete="email" className={inputClass} {...field} />
            </FormControl>

            <FormMessage className="mt-1 text-[12px]" />
          </FormItem>} />

      <FormField control={control} name="country" rules={{
      required: "Please select your country"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className={fieldLabelClass}>
              Country of Residence
            </FormLabel>

            <Select value={field.value} onValueChange={field.onChange}>
              <FormControl>
                <SelectTrigger className={selectTriggerClass}>
                  <SelectValue placeholder="Select a country" />
                </SelectTrigger>
              </FormControl>

              <SelectContent className="border-[#D9CFBE] bg-[#FFFDF9]">
                {COUNTRIES.map((country, index) => <SelectItem key={country} value={country} data-index={index} className="text-[#073F31] focus:bg-[#EFF4E4] focus:text-[#073F31]">
                    {country}
                  </SelectItem>)}
              </SelectContent>
            </Select>

            <FormMessage className="mt-1 text-[12px]" />
          </FormItem>} />

      <FormField control={control} name="playingLevel" rules={{
      required: "Please select your playing level"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className={fieldLabelClass}>
              Playing Level
            </FormLabel>

            <Select value={field.value} onValueChange={field.onChange}>
              <FormControl>
                <SelectTrigger className={selectTriggerClass}>
                  <SelectValue placeholder="Select your level" />
                </SelectTrigger>
              </FormControl>

              <SelectContent className="border-[#D9CFBE] bg-[#FFFDF9]">
                {PLAYING_LEVELS.map((level, index) => <SelectItem key={level} value={level} data-index={index} className="text-[#073F31] focus:bg-[#EFF4E4] focus:text-[#073F31]">
                    {level}
                  </SelectItem>)}
              </SelectContent>
            </Select>

            <FormMessage className="mt-1 text-[12px]" />
          </FormItem>} />

      <FormField control={control} name="travelInterests" rules={{
      required: "Please select your primary interest"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className={fieldLabelClass}>
              Primary Travel Interest
            </FormLabel>

            <Select value={field.value} onValueChange={field.onChange}>
              <FormControl>
                <SelectTrigger className={selectTriggerClass}>
                  <SelectValue placeholder="Select an interest" />
                </SelectTrigger>
              </FormControl>

              <SelectContent className="border-[#D9CFBE] bg-[#FFFDF9]">
                {TRAVEL_INTERESTS.map((interest, index) => <SelectItem key={interest} value={interest} data-index={index} className="text-[#073F31] focus:bg-[#EFF4E4] focus:text-[#073F31]">
                    {interest}
                  </SelectItem>)}
              </SelectContent>
            </Select>

            <FormMessage className="mt-1 text-[12px]" />
          </FormItem>} />

      <FormField control={control} name="consent" rules={{
      required: "You must agree before submitting"
    }} render={({
      field
    }) => <FormItem className="rounded-[10px] border border-[#DDD3C2] bg-[#FAF7EF] p-4">
            <div className="flex flex-row items-start gap-3">
              <FormControl>
                <Checkbox checked={field.value} onCheckedChange={field.onChange} className="mt-0.5 size-[18px] rounded-full border-[#B88F46] data-[state=checked]:border-[#073F31] data-[state=checked]:bg-[#073F31] data-[state=checked]:text-[#FFFDF7]" />
              </FormControl>

              <div className="min-w-0 flex-1">
                <FormLabel className="cursor-pointer text-[12px] font-normal leading-[1.65] text-[#526B64]">
                  I agree to receive relevant golf travel updates,
                  destination inspiration and newsletter communications from
                  TeeListClub.
                </FormLabel>

                <FormMessage className="mt-2 text-[12px]" />
              </div>
            </div>
          </FormItem>} />

      {submitError && <Alert variant="destructive" className="rounded-[10px]">
          <AlertTitle>Unable to submit</AlertTitle>
          <AlertDescription>{submitError}</AlertDescription>
        </Alert>}

      <Button type="submit" disabled={isSubmitting} className="group h-[54px] w-full rounded-[8px] bg-[#BFFF00] px-6 text-[12px] font-bold uppercase tracking-[0.09em] text-[#073F31] shadow-[0_10px_26px_rgba(7,63,49,0.1)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#B4F000] hover:text-[#073F31] hover:shadow-[0_14px_32px_rgba(7,63,49,0.16)] disabled:translate-y-0 disabled:opacity-65">
        {isSubmitting ? <>
            <Loader2 aria-hidden="true" className="mr-2 size-4 animate-spin" />
            Processing...
          </> : <>
            Join the Club

            <ArrowRight aria-hidden="true" className="ml-2 size-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.8} />
          </>}
      </Button>

      <div className="flex items-center justify-center gap-2 pt-1 text-center text-[11px] leading-[1.5] text-[#7B8883]">
        <LockKeyhole aria-hidden="true" className="size-3.5 shrink-0 text-[#B88F46]" strokeWidth={1.7} />

        <span>
          We respect your privacy. Your information is safe with us.
        </span>
      </div>
    </Form>;
}
export default function JoinTeeListClubForm() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section id="join-teelistclub-form" aria-labelledby="join-teelistclub-form-title" className="relative overflow-hidden bg-[#FFFDF7] py-20 text-[#073F31] md:py-24 lg:py-28">
      {/* Subtle warm paper texture */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.2]" style={{
      backgroundImage: "radial-gradient(circle at 18% 20%, rgba(184,143,70,0.055) 0, rgba(184,143,70,0.055) 1px, transparent 1px), radial-gradient(circle at 82% 72%, rgba(7,63,49,0.028) 0, rgba(7,63,49,0.028) 1px, transparent 1px)",
      backgroundSize: "27px 27px, 33px 33px"
    }} />

      {/* Soft central highlight */}
      <div aria-hidden="true" className="pointer-events-none absolute left-1/2 top-[52%] h-[560px] w-[760px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#F3EAD8]/55 blur-3xl" />

      <div ref={ref} className={`relative mx-auto w-full max-w-[1480px] px-5 transition-all duration-700 ease-out sm:px-8 md:px-12 lg:px-16 ${isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
        <div className="mx-auto max-w-[820px]">
          <div className="mb-10 text-center md:mb-12">
            <p className="mb-4 uppercase tracking-[0.24em] text-[#B88F46] !text-sm !font-bold" style={{
            fontFamily: "'Raleway', Arial, sans-serif"
          }}><span className="!text-sm">Join Our Community</span></p>

            <h2 id="join-teelistclub-form-title" className="font-normal leading-[1.08] tracking-[-0.035em] text-[#073F31]" style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            fontSize: "clamp(2.8rem, 4.3vw, 4.65rem)"
          }}>
              Join TeeListClub
              
            </h2>

            <div aria-hidden="true" className="mx-auto mt-6 h-px w-12 bg-[#B88F46]" />

            <p className="mx-auto mt-6 max-w-[600px] text-[15px] leading-[1.8] text-[#5B716A] sm:text-[16px]" style={{
            fontFamily: "'Raleway', Arial, sans-serif"
          }}>
              Tell us a little about how you play and travel so TeeListClub can
              share more relevant course discoveries, destination ideas and
              member updates.
            </p>
          </div>

          <div className="rounded-[16px] border border-[#DDD2C0] bg-[#FFFDF9]/95 p-5 shadow-[0_20px_55px_rgba(43,54,49,0.08)] backdrop-blur-sm sm:p-7 md:p-9 lg:p-10">
            <FormProvider formId="teelistclub_join" sectionName="JoinTeeListClubForm" defaultValues={{
            firstName: "",
            lastName: "",
            email: "",
            country: "",
            playingLevel: "",
            travelInterests: "",
            consent: false
          }} submitText="Join the Club" successMessage="Thank you for joining TeeListClub! We have received your registration and will be in touch with relevant golf, course and destination updates.">
              <RegistrationFormInner />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}